import React, {useEffect, useRef, useState} from "react";
import {ATTEndpoints} from "../../../../../services/att/endpoints";
import CustomDataGrid, {DataGridRef} from "../../../../../components/data-grid/CustomDataGrid";
import {
  GridColDef,
  GridFilterInputValueProps,
  GridFilterItem,
  GridFilterModel,
  GridFilterOperator,
  GridRenderCellParams, GridSelectionModel,
} from "@mui/x-data-grid";
import {Box, FormControl, Grid, MenuItem, Stack, TextField, Tooltip,} from "@mui/material";
import {DatePicker} from "@mui/x-date-pickers";
import {useTranslation} from "react-i18next";
import WebURL from "@/urls";
import CustomBreadcrumbs from "@/components/breadcrumbs/CustomBreadcrumbs";
import SnackbarUtil from "@/utils/SnackbarUtil";
import CustomRowButton from "@/components/data-grid/CustomRowButton";
import {ReactComponent as IconView} from "@/assets/icons/IcView.svg";
import {ReactComponent as IconDeletes} from "@/assets/icons/IconDeletes.svg";
import Dialog from "@/components/dialog";
import {useLocation, useNavigate} from "react-router-dom";
import PunchRecordService from "@/services/att/services/PunchRecordService";
import CompanyService from "@/services/auth/CompanyService";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import PunchApproval from "@/pages/att/Timecard/components/ManualLog/components/Approval";
import {ReactComponent as ApprovalButton} from "@/assets/manual/ApprovalButton.svg";
import {ReactComponent as RejectButton} from "@/assets/manual/RejectButton.svg";


const SUBMIT_FILTER_STROKE_TIME = 500;

interface InputNumberIntervalExtraProps {
  dateFormat: string;
}

type InputNumberIntervalProps = GridFilterInputValueProps & InputNumberIntervalExtraProps;

function InputNumberInterval(props: InputNumberIntervalProps) {
  const {t} = useTranslation();
  const {item, applyValue, dateFormat} = props;

  const filterTimeout = useRef<any>();
  const [filterValueState, setFilterValueState] = useState<[Date, Date]>([item.value[0], item.value[1]]);

  useEffect(() => {
    return () => {
      clearTimeout(filterTimeout.current);
    };
  }, []);

  const updateFilterValue = (lowerBound: Date, upperBounding: Date) => {
    clearTimeout(filterTimeout.current);
    setFilterValueState([lowerBound, upperBounding]);

    filterTimeout.current = setTimeout(() => {
      applyValue({...item, value: [lowerBound, upperBounding]});
    }, SUBMIT_FILTER_STROKE_TIME)
  };

  const handleUpperFilterChange = (event: any) => {
    const newUpperBound = event;
    updateFilterValue(filterValueState[0], newUpperBound);
  };
  const handleLowerFilterChange = (event: any) => {
    const newLowerBound = event;
    updateFilterValue(newLowerBound, filterValueState[1]);
  };

  return (
    <Box
      sx={{
        display: 'inline-flex',
        flexDirection: 'row',
        alignItems: 'end',
        width: "240px"
      }}
    >
      <DatePicker
        label={t("common.Start Date")}
        value={filterValueState[0] || new Date(new Date().getFullYear(), new Date().getMonth(), 1)}
        maxDate={filterValueState[1]}
        inputFormat={dateFormat}
        onChange={handleLowerFilterChange}
        renderInput={(params) =>
          <TextField
            variant="standard"
            {...params}
          />}
      />
      <DatePicker
        label={t("common.End Date")}
        value={filterValueState[1]}
        minDate={filterValueState[0]}
        inputFormat={dateFormat}
        onChange={handleUpperFilterChange}
        renderInput={(params) =>
          <TextField
            variant="standard"
            {...params}
          />}
      />
    </Box>
  )
}

const ManualLogGrid: React.FC = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const today = new Date()
  const firstDay = new Date()
  firstDay.setDate(1)
  const dataGridRef = useRef<DataGridRef>();
  const manualApprovalRef = useRef<any>({})
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const [filterParams, setFilterParams] = useState<Record<any, any>>({
    date_range: [
      firstDay.toISOString().slice(0, 10),
      today.toISOString().slice(0, 10)
    ],
    approval_status: ''
  });
  const [filterDate, setFilterDate] = useState<GridFilterModel>({
    items: [
      {
        id: 1,
        columnField: 'att_date',
        value: [firstDay.toISOString().slice(0, 10), today.toISOString().slice(0, 10)],
        operatorValue: "between",
      }
    ]
  })
  const [dateFormat, setDateFormat] = useState<string>("MM/dd/yyyy")
  const [punchStatus, setPunchStatus] = React.useState<string>("");
  const PUNCH_FROM_APPROVAL_STATUS = {
    'pending': t("common.Pending"),
    'approved': t("common.Approved"),
    'rejected': t("common.Rejected"),
    'withdrawn': t("common.Withdrawn"),
  }
  useEffect(() => {
    CompanyService.dateFormat({method: 'get'}).then((res) => {
      const date_format = res.data?.date_format?.toString() || "MM/dd/yyyy";
      setDateFormat(date_format);
    });
  }, [])
  const location = useLocation();
  const reloadTable = () => {
    dataGridRef.current?.refresh();
  }
  useEffect(() => {
    reloadTable();
    if (location.state?.needRefresh) {
      reloadTable();
      location.state.needRefresh = false;
    }
  }, [location.state?.needRefresh]);
  const WrappedInputNumberInterval = (props: GridFilterInputValueProps) => {
    // const dateFormat = "MM/dd/yyyy";

    return <InputNumberInterval {...props as InputNumberIntervalProps} dateFormat={dateFormat}/>;
  };
  const punchDateOperators: GridFilterOperator[] = [
    {
      label: t("common.Between"),
      value: "between",
      getApplyFilterFn: (filterItem: GridFilterItem, column) => {
        if (!Array.isArray(filterItem.value) || filterItem.value.length !== 2) {
          return null;
        }
        return ({value}) => {
          return true;
        };
      },
      InputComponent: WrappedInputNumberInterval
    },
  ];
  const SourceField = (params: GridRenderCellParams) => {
    let punch_from = "";
    if (params.value == 'manual') {
      punch_from = t("common.manual");
    } else {
      punch_from = params.value
    }
    return <Grid>{punch_from}</Grid>;
  };

  const StatusField = (params: GridRenderCellParams) => {
    let approval_status = "";
    if (params.value == '0') {
      approval_status = '-';
      return <Grid>{approval_status}</Grid>
    } else {
      switch (params.value) {
        case 'pending':
          return <Grid sx={{color: "#2BA9DE"}}>{t("common.Pending")}</Grid>
        case 'approved':
          return <Grid sx={{color: "#7AC143"}}>{t("common.Approved")}</Grid>
        case 'rejected':
          return <Grid sx={{color: "#F2453B"}}>{t("common.Rejected")}</Grid>
        case 'withdrawn':
          return <Grid sx={{color: "#FCA265"}}>{t("common.Withdrawn")}</Grid>
      }
    }
  };
  const RemarksField = (params: GridRenderCellParams) => {
    return (
      <Box sx={{
        position: "static",
        left: "12px",
        top: "14px",
        width: "202px",
        height: "48px",
        opacity: 1,
        fontFamily: "Alibaba PuHuiTi 2.0",
        fontSize: "14px",
        fontWeight: "normal",
        lineHeight: "16px",
        letterSpacing: "0em",
        fontVariationSettings: '"opsz" auto',
        color: "#3D3D3D",
        zIndex: 0,
        display: "flex",
        alignItems: "flex-start",
        flexWrap: 'wrap',
        wordBreak: 'break-word',
        whiteSpace: 'normal',
        overflow: 'hidden',
      }}>
        <Tooltip title={`${params.value}`} arrow>
          {params.value}
        </Tooltip>
      </Box>
    );
  }
  const PunchTimeField = (params: GridRenderCellParams) => {
    return <Grid>{params.value.slice(11, 19)}</Grid>;
  };

  const columns: GridColDef[] = [
    {
      field: 'employee_name', headerName: t("common.Person Name"), minWidth: 150, flex: 0.2, filterable: false,
      renderCell: (params) => {
        return (
          <Grid style={{display: "flex", alignItems: "center"}}>
            {/* <AvatarIcon name={params.row.first_name} /> */}
            <Box style={{padding: '0 8px'}}>{params.row.first_name} {params.row.last_name}</Box>
          </Grid>
        )
      }
    },
    {
      field: 'employee_code',
      headerName: t("common.Person ID"),
      type: "string",
      minWidth: 150,
      flex: 0.2,
      filterable: false
    },
    {
      field: 'att_date', headerName: t("common.Punch Date"), type: "date", minWidth: 130, flex: 0.3,
      filterOperators: punchDateOperators,
    },
    {
      field: 'punch_time',
      headerName: t("common.Attendance record"),
      flex: 0.3,
      minWidth: 200,
      renderCell: PunchTimeField,
      filterable: false,
    },
    {field: 'timezone', headerName: t("att.schedule.TimeZone"), minWidth: 100, flex: 0.2, filterable: false},
    {
      field: 'punch_from',
      headerName: t("common.punch from"),
      flex: 0.2,
      minWidth: 100,
      renderCell: SourceField,
      filterable: false
    },
    {
      field: 'approval_status',
      headerName: t("common.approval status"),
      flex: 0.1,
      minWidth: 100,
      renderCell: StatusField,
      filterable: false
    },
    {
      field: 'remarks',
      headerName: t("common.remarks"),
      flex: 0.3,
      minWidth: 150,
      renderCell: RemarksField,
      filterable: false
    },
    {
      field: 'actions', flex: 0.4, headerName: t("common.action"), headerAlign: 'left', align: 'left',
      sortable: false, filterable: false,
      renderCell: (e) => {
        return (
          <Stack direction={"row"}>
            <Grid sx={{marginLeft: "-5px"}}>
              <CustomRowButton
                insideSvgIcon={IconView}
                actionName={t("common.view")}
                buttonProps={{
                  key: "view",
                  onClick: () => {
                    manualApprovalRef?.current?.showView(e.row.id)
                  }
                }}
              />
            </Grid>
            {e.row.punch_state == '0' && <Grid>
              <CustomRowButton
                insideSvgIcon={IconDeletes}
                actionName={t("common.delete")}
                buttonProps={{
                  key: "delete",
                  onClick: () => (
                    Dialog.confirm({
                      title: t("common.Delete Punch Record?"),
                      content: t("common.Manual Log Delete Note."),
                      onConfirm: () => {
                        PunchRecordService.destroy((e.id).toString()).then(
                          (result) => {
                            SnackbarUtil.success(t('common.success'));
                            dataGridRef.current?.refresh();
                          });
                      }
                    })
                  )
                }}
              />
            </Grid>}
            {['pending', 'withdrawn_need_approve'].includes(e.row.approval_status) && <Grid sx={{marginLeft: "-5px"}}>
              <CustomRowButton
                insideSvgIcon={ApprovalButton}
                actionName={t("common.Approval")}
                buttonProps={{
                  key: "Approval",
                  onClick: () => {
                    manualApprovalRef?.current?.showDetail(e.id)
                  }
                }}
              />
            </Grid>}
          </Stack>
        )
      }
    }
  ]

  const toolbar = {
    title: t("common.Mend attendance punch"),
    searchTips: t("common.Search by Person ID, Person Name"),
    search: true,
    // info: {setDisplay: setInfo,},
    breadcrumbs: [
      {breadcrumbName: t("common.Mend attendance punch"), path: WebURL.ATT_TIMECARD_MANUAL_LOG}
    ],
    extraComponentOnRight:
      <Grid
        sx={{
          display: "flex",
          alignItems: "center",
          marginBottom: "2px",
          marginRight: "-20px"
        }}
      >
        <CustomRowButton
          insideSvgIcon={ApprovalButton}
          actionName={t("common.Approval")}
          buttonProps={{
            key: "Multi Approval",
            onClick: () => {
              if (selectionModel.length == 0) {
                SnackbarUtil.error(t("common.Select Punch Records first"),
                  {anchorOrigin: {vertical: 'top', horizontal: 'center'}})
                return;
              } else {
                Dialog.confirm({
                  title: t("common.Approve Punch Record?"),
                  content: t("common.Are you sure you want to approve to these Punch records?"),
                  onConfirm: () => {
                    const data = {
                      "ids": selectionModel
                    }
                    PunchRecordService.multiApprove(data).then((res) => {
                      if (res.status < 400) {
                        SnackbarUtil.success(t("common.success"));
                        reloadTable()
                      } else {
                        SnackbarUtil.error(res.data.message,
                          {anchorOrigin: {vertical: 'top', horizontal: 'center'}})
                        return;
                      }
                    })
                  }
                })
              }
            }
          }}
        />
        <CustomRowButton
          insideSvgIcon={RejectButton}
          actionName={t("common.Reject")}
          buttonProps={{
            key: "Multi Reject",
            onClick: () => {
              if (selectionModel.length == 0) {
                SnackbarUtil.error(t("common.Select Punch Records first"),
                  {anchorOrigin: {vertical: 'top', horizontal: 'center'}})
                return;
              } else {
                Dialog.confirm({
                  title: t("common.Reject Punch Record?"),
                  content: t("common.Are you sure you want to reject to these Punch records?"),
                  onConfirm: () => {
                    const data = {
                      "ids": selectionModel
                    }
                    PunchRecordService.multiReject(data).then((res) => {
                      if (res.status < 400) {
                        SnackbarUtil.success(t("common.success"));
                        reloadTable()
                      } else {
                        SnackbarUtil.error(res.data.message,
                          {anchorOrigin: {vertical: 'top', horizontal: 'center'}})
                        return;
                      }
                    })
                  }
                })
              }
            }
          }}
        />
      </Grid>,
    extraComponent:
      <>
        <Grid sx={{paddingRight: "1vw"}}>
          <FormControl fullWidth sx={{
            marginLeft: "10px",
            width: "180px",
            // 确保标签容器居中
            display: 'flex',
            justifyContent: 'center'
          }}>
            <InputLabel
              id="verify-type-select-helper-label"
              sx={{
                textAlign: "center",
                width: '100%',
                left: 0,
                top: '50%',
                transform: 'translate(0, -50%) scale(0.85)',
                '&.MuiInputLabel-shrink': {
                  textAlign: "left",
                  left: 10,
                  top: 0,
                  transform: 'translate(0, -50%) scale(0.85)',
                },
                '&.Mui-focused': {
                  textAlign: "left",
                  left: 10,
                  top: 0,
                  transform: 'translate(0, -50%) scale(0.85)',
                }
              }}
            >
              {t('common.approval status')}
            </InputLabel>
            <Select
              labelId="punch-status-select-label"
              id="punch-status-select"
              value={punchStatus}
              label={t('common.approval status')}
              size={'medium'}
              onChange={(e) => {
                setPunchStatus(e.target.value)
                setFilterParams({...filterParams, approval_status: e.target.value})
              }}
              sx={{
                '& .MuiSelect-select': {
                  textAlign: 'center'
                }
              }}
            >
              <MenuItem key={''} value={t("common.All status")}>{t("common.All status")}</MenuItem>
              {Object.entries(PUNCH_FROM_APPROVAL_STATUS).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </>
    ,
    actions: [{
      key: 'add', iconFont: 'icon-ic_add', helpTxt: t("common.add"), callback: async () => {
        navigate(WebURL.ATT_TIMECARD_PUNCH_RECORD_ADD);
      }
    }],
  }

  function debounce(fn: any, delay: any) {
    let timer: any;
    return function (...args: any) {
      if (timer) {
        clearTimeout(timer);
      }
      timer = setTimeout(() => {
        fn(...args);
      }, delay);
    };
  }

  const debouncedSave = debounce((newValue: any) => {
    reloadTable()
  }, 1000);

  useEffect(() => {
    debouncedSave(punchStatus);
  }, [punchStatus]);

  return (
    <>
      <Box
        sx={{
          '& .MuiDataGrid-row-height-no-limit': {
            maxHeight: '180px!important'
          },
        }}
      >
        <CustomBreadcrumbs record={[{label: t("common.Mend attendance punch")}]}/>
        <CustomDataGrid
          columns={columns}
          ref={dataGridRef}
          uri={ATTEndpoints.ATT_MANUAL_LOG_URI}
          // checkboxSelection={true}
          requestParams={filterParams}
          filterModel={filterDate}
          checkboxSelection={true}
          onSelectionModelChange={(newSelectionModel) => {
            setSelectionModel(newSelectionModel);
          }}
          onFilterModelChange={(model) => {
            if (typeof (model.items[0].value[0]) == 'object') {
              model.items[0].value[0] = model.items[0].value[0].toISOString().slice(0, 10)
            }
            if (typeof (model.items[0].value[1]) == 'object') {
              model.items[0].value[1] = model.items[0].value[1].toISOString().slice(0, 10)
            }
            setFilterDate(model)
            setFilterParams({
              date_range: [
                model.items[0].value[0],
                model.items[0].value[1]
              ],
              approval_status: punchStatus,
            })
            dataGridRef.current?.refresh(true);
          }}
          toolbar={toolbar}
          localeText={{
            filterPanelColumns: t("common.Columns"),
            filterPanelOperators: t("common.Operators"),
          }}
        />
        {/*<PunchView ref={manualViewRef} reloadTable={reloadTable} dateFormat={dateFormat}></PunchView>*/}
        <PunchApproval ref={manualApprovalRef} reloadTable={reloadTable}/>
      </Box>
    </>
  )
}
export default ManualLogGrid
